import { useEffect } from "react";
import { useRoutedNavigation } from "presentation/hooks/useRoutedNavigation";
import { Grid, CircularProgress, Box, Typography } from "@mui/material";
import {
  CheckAllNewsButton,
  CheckNewsDetails,
  HomeSectionTitle,
  NewsDescription,
  NewsImage,
  NewsTitle,
  PageSection,
  PaginationButton,
  PaginationNumber,
} from "../home.styles";
import { ReactComponent as Next } from "assets/icons/arrow-next.svg";
import { ReactComponent as Prev } from "assets/icons/arrow-prev.svg";
import { NewCategory } from "presentation/components/shared/categoryNew";
import { useHomeState } from "../context/home-context";
import { useHomeController } from "../context/home-controller";

interface SectionNewsHighlightsProps {
  title?: string;
  /** Quantidade de notícias no carrossel (home customizada); padrão: todas as carregadas. */
  quantity?: number;
}

export default function SectionNewsHighlights({ title, quantity }: SectionNewsHighlightsProps = {}) {
  const { news: loadedNews, activeNewsSlide, loadingNews, errorNews } = useHomeState();
  const news = quantity ? loadedNews.slice(0, quantity) : loadedNews;
  const { setActiveNewsSlide } = useHomeController();
  const { navigateTo: navigate } = useRoutedNavigation();

  useEffect(() => {
    if (news.length === 0) return;

    const interval = setInterval(() => {
      if (activeNewsSlide === news.length - 1) {
        setActiveNewsSlide(0);
      } else {
        setActiveNewsSlide(activeNewsSlide + 1);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [activeNewsSlide, news.length, setActiveNewsSlide]);

  const handleClick = (operation: string) => {
    if (operation === "next") {
      const nextSlide = activeNewsSlide === news.length - 1 ? activeNewsSlide : activeNewsSlide + 1;
      setActiveNewsSlide(nextSlide);
    } else {
      const prevSlide = activeNewsSlide === 0 ? 0 : activeNewsSlide - 1;
      setActiveNewsSlide(prevSlide);
    }
  };

  if (loadingNews) {
    return (
      <PageSection>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <HomeSectionTitle>
              {title || 'Últimas Notícias'} <hr />
            </HomeSectionTitle>
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
            </Box>
          </Grid>
        </Grid>
      </PageSection>
    );
  }

  if (errorNews || news.length === 0) {
    return (
      <PageSection>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <HomeSectionTitle>
              {title || 'Destaques'} <hr />
            </HomeSectionTitle>
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <Typography color="text.secondary">
                {errorNews || "Nenhuma notícia disponível no momento"}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </PageSection>
    );
  }

  const currentNews = news[activeNewsSlide];

  return (
    <PageSection>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <HomeSectionTitle>
            {title || 'Destaques'} <hr />
          </HomeSectionTitle>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={1} direction="column">
            <Grid item xs={12}>
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  {currentNews.urlImageEmphasis ? (
                    <NewsImage
                      src={currentNews.urlImageEmphasis}
                      alt={currentNews.title || "Notícia"}
                      onError={(e: any) => {
                        e.target.style.display = 'none';
                        const fallbackBox = e.target.nextSibling;
                        if (fallbackBox) fallbackBox.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <Box
                    sx={{
                      width: "100%",
                      height: "400px",
                      backgroundColor: "#f5f5f5",
                      display: currentNews.urlImageEmphasis ? "none" : "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "8px",
                    }}
                  >
                    <Typography color="text.secondary">
                      Sem imagem
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Grid
                    container
                    spacing={2}
                    direction="column"
                    alignItems="stretch"
                  >
                    {currentNews.category && (
                      <Grid item xs={12}>
                        <NewCategory>{currentNews.category}</NewCategory>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <NewsTitle>{currentNews.title}</NewsTitle>
                    </Grid>
                    <Grid item xs={12}>
                      <NewsDescription>
                        {currentNews.desFont || "Confira os detalhes desta notícia."}
                      </NewsDescription>
                    </Grid>
                    <Grid item xs={12}>
                      <Grid container spacing={2}>
                        <Grid item xs="auto">
                          <CheckAllNewsButton
                            onClick={() => navigate("/noticias")}
                          >
                            Ver todas as notícias
                          </CheckAllNewsButton>
                        </Grid>
                        <Grid item xs="auto">
                          <CheckNewsDetails
                            onClick={() =>
                              navigate(`/noticias/${currentNews.id}`)
                            }
                          >
                            Saiba mais
                          </CheckNewsDetails>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid
                container
                spacing={1}
                justifyContent="flex-end"
                alignItems="center"
              >
                <Grid item xs="auto">
                  <PaginationButton
                    onClick={() => handleClick("prev")}
                    disabled={activeNewsSlide === 0}
                  >
                    <Prev />
                  </PaginationButton>
                </Grid>
                <Grid item xs="auto">
                  <PaginationNumber isActive>{activeNewsSlide + 1}</PaginationNumber>
                </Grid>
                <Grid item xs="auto">
                  <PaginationNumber>
                    {activeNewsSlide < news.length - 1 ? activeNewsSlide + 2 : 1}
                  </PaginationNumber>
                </Grid>
                <Grid item xs="auto">
                  <PaginationButton
                    onClick={() => handleClick("next")}
                    disabled={activeNewsSlide === news.length - 1}
                  >
                    <Next />
                  </PaginationButton>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </PageSection>
  );
}
